import { Envelope, Clock, User } from "@/components/ui/icons";
import { CardShell } from "./CardShell";
import { CardActions } from "./CardActions";

export interface EmailData {
  messageId: string;
  from: string;
  to: string;
  subject: string;
  date: string;
  body: string;
}

interface EmailCardProps {
  email: EmailData;
  /** When true, renders in compact mode for chat messages (no drag handle) */
  compact?: boolean;
  density?: "ultra-compact" | "compact" | "comfortable";
}

export function EmailCard({ email, compact, density }: EmailCardProps) {
  const formattedDate = formatDate(email.date);

  return (
    <CardShell
      compact={compact}
      density={density}
      icon={<Envelope size={12} className="text-success" />}
      label="email"
      telemetryCardType="email"
      headerActions={
        <CardActions
          compact={compact}
          title={email.subject}
          summary={email.body}
          url={
            email.messageId
              ? `https://mail.google.com/mail/u/0/#inbox/${email.messageId}`
              : null
          }
        />
      }
      contentClassName={`${
        compact || density === "ultra-compact"
          ? "px-2 py-1.5 space-y-1"
          : density === "compact"
            ? "px-2.5 py-2 space-y-1.5"
            : "px-3 py-2.5 space-y-2"
      }`}
    >
      {/* Subject */}
      <div className="text-foreground font-bold whitespace-pre-wrap break-words">
        {email.subject || "(no subject)"}
      </div>

      {/* From */}
      <div className="flex items-center gap-1.5">
        <User size={10} className="text-muted-foreground shrink-0" />
        <span className="text-foreground whitespace-pre-wrap break-words min-w-0">
          {email.from}
        </span>
      </div>

      {/* Date */}
      <div className="flex items-center gap-1.5">
        <Clock size={10} className="text-info shrink-0" />
        <span className="text-foreground/70">{formattedDate}</span>
      </div>

      {/* Full body — scrollable */}
      {email.body && (
        <div className="text-foreground/80 whitespace-pre-wrap break-words border-t border-border pt-1.5 mt-1.5 leading-relaxed max-h-64 overflow-y-auto">
          {email.body}
        </div>
      )}
    </CardShell>
  );
}

/** Parse Gmail tool output into EmailData[] */
export function parseGmailResult(result: string): EmailData[] | null {
  try {
    const parsed = JSON.parse(result);
    const data =
      parsed?.data ??
      parsed?.response_data?.data ??
      parsed?.response_data ??
      parsed;

    const messages: unknown[] = Array.isArray(data?.messages)
      ? data.messages
      : Array.isArray(data)
        ? data
        : [];

    if (messages.length === 0) return null;

    const parsedEmails = messages
      .map((msg) => {
        if (!msg || typeof msg !== "object") return null;
        const m = msg as Record<string, unknown>;

        const headers = Array.isArray(
          (m.payload as Record<string, unknown>)?.headers,
        )
          ? ((m.payload as Record<string, unknown>).headers as Array<{
              name: string;
              value: string;
            }>)
          : [];

        const getHeader = (name: string) =>
          headers.find((h) => h.name.toLowerCase() === name.toLowerCase())
            ?.value ?? "";

        // Extract plain text body — strip HTML
        let body = "";
        if (typeof m.messageText === "string") {
          body = stripHtml(m.messageText);
        } else if (typeof m.snippet === "string") {
          body = m.snippet as string;
        }

        return {
          messageId: (m.messageId as string) ?? "",
          from: getHeader("From") || (m.sender as string) || "",
          to: getHeader("To") || (m.recipient as string) || "",
          subject:
            getHeader("Subject") || (m.subject as string) || "(no subject)",
          date: (m.messageTimestamp as string) || getHeader("Date") || "",
          body,
        } satisfies EmailData;
      })
      .filter((e): e is EmailData => e !== null);
    if (parsedEmails.length === 0) return null;

    // Some Gmail providers can return duplicate rows for the same message.
    // Keep first-seen order and dedupe primarily by messageId.
    const seen = new Set<string>();
    const deduped = parsedEmails.filter((email) => {
      const key =
        email.messageId?.trim() ||
        `${email.subject}|${email.from}|${email.date}|${email.body.slice(0, 120)}`;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return deduped.length > 0 ? deduped : null;
  } catch {
    return null;
  }
}

function stripHtml(text: string): string {
  // If the text looks like full HTML, parse with DOMParser for clean extraction
  if (/<(!doctype|html|head|body)/i.test(text)) {
    try {
      const doc = new DOMParser().parseFromString(text, "text/html");
      // Remove style/script elements entirely
      doc.querySelectorAll("style, script, head").forEach((el) => el.remove());
      const clean = (doc.body?.textContent ?? "")
        .replace(/\r\n/g, "\n")
        .replace(/[ \t]+/g, " ")
        .replace(/\n /g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
      if (clean) return clean;
    } catch {
      // Fall through to regex approach
    }
  }

  // Remove <style>...</style> and <script>...</script> blocks
  let cleaned = text
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "");
  // Strip remaining tags
  cleaned = cleaned
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/?(p|div|tr|li|h[1-6])[^>]*>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&[a-z]+;/gi, "")
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+/g, " ")
    .replace(/\n /g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
  return cleaned;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateStr;
  }
}
